import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
// StyledLinkAnimation no longer used — layout moved to CSS modules
// PopUp removed — replaced with antd message toasts
import DeliverableService from "../../../features/DeliverableView/services/DeliverableService";
import { getFilteredKeys } from "../../../core/services/SessionStorageService";
// ThemePalette no longer used directly
import {
  ACTIVE_TABS_KEY,
  ADDROW_TEXT,
  CANCEL_TEXT,
  DASHBOARD,
  EDIT_ONLINE_TEXT,
  EXPORT_BUTTON_LABEL,
  FILTER_TEMPLATE_LABEL,
  IMPORT_BUTTON_LABEL,
  IMPORT_FAILED_MESSAGE,
  MULTI_SHEET_KEY,
  NAME_KEY,
  SAVE_TEXT,
  SESSION_USERNAME,
  TASK_POLLING_INTERVAL,
  TaskStatus,
  TaskType,
  TEMPLATE,
  TOAST_DEFAULT_ERROR_MESSAGE,
  USER_ACCESS,
} from "../../utils/CommonConstants";
import { generateDefaultFilterValues } from "../../utils/FilterKeysHelper";
import { DeliverableMenuDetailsResponse } from "../DeliverableDetailsComponent/DeliverableDetailsComponent.types";
import { onHandleExportClick } from "../ExcelViewComponent/ExcelViewComponent.helper";
// Styled-components no longer used — layout moved to CSS modules
import { CloseOutlined, LogoutOutlined, MenuOutlined } from "@ant-design/icons";
import { Button, ConfigProvider, Dropdown, Form, Input, MenuProps, message, Modal } from "antd";
import {
  DeliverableMenuSheet,
  UniqueSequenceDtos,
} from "./DeliverableMenuComponent.types";
import { SearchInput } from "../../../shared/base-components/Search/Search";
import { LogOut } from "../../../features/AuthenticateView/pages/Login/Login.types";
import { LogoutService } from "../../../features/AuthenticateView/services/LoginService";
import { REFRESH_TOKEN_KEY, TOKEN_KEY } from "../../utils/SessionConstants";
import { useAgGridContext } from "../AgGridComponent/AgGridContext";
import { useSpreadWrapper } from "../ExcelViewComponent/SpreadWrapperContext";
import { darkFormTheme, MODAL_STYLES, MODAL_TITLE_STYLE } from "../../../styles/modalStyles";
import styles from "./DeliverableMenu.module.css";
import layout from "./DeliverableMenuLayout.module.css";
import { BtnContent } from "./ButtonLoaderSpinnder";

export const DeliverableMenu = () => {
  const location = useLocation();
  const [selectedLink, setSelectedLink] = useState<string | null>(null);
  const projectService: DeliverableService = new DeliverableService();
  const [searchParams] = useSearchParams();
  const [isMenuOpened, setIsMenuOpened] = useState(false);
  const { spreadWrapperRef } = useSpreadWrapper();
  const { agGridRef } = useAgGridContext();
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  // overlay states removed — using message toasts
  const [showAddRow, setShowAddRow] = useState<boolean>(false);
  const [showAddNewRow, setShowAddNewRow] = useState<boolean>(false);
  const [form] = Form.useForm();
  // taskStatus removed — using message toasts
  const [searchValue, setSearchValue] = useState<string>("");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isAgGridImportRef = useRef<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [additionalTabsVisible, setAdditionalTabsVisible] =
    useState<boolean>(true);
  const [deliverableName, setDeliverableName] = useState<string | null>("");
  const [deliverableDetails, setDeliverableDetails] =
    useState<DeliverableMenuDetailsResponse | null>(null);
  const [deliverableMenuDetails, setDeliverableMenuDetails] = useState<
    DeliverableMenuSheet[]
  >([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [uniqueSequenceDtos, setUniqueSequenceDtos] = useState<
    UniqueSequenceDtos[]
  >([]);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [isMultisheet, setIsMultisheet] = useState<boolean>(false);
  const { deliverableId = "-1", sheetId = "-1" } = useParams();
  const navigate = useNavigate();
  const sheetNameFromParams = searchParams.get(ACTIVE_TABS_KEY);
  const hasImportPermission = true;
  const fromProjectIdRef = useRef<string | null>(null);
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {},
  );
  const setLoading = (key: string, val: boolean) =>
    setLoadingStates((prev) => ({ ...prev, [key]: val }));

  useEffect(() => {
    if (location.state?.fromProjectId) {
      fromProjectIdRef.current = location.state.fromProjectId;
    }
  }, [location.state]);

  const handleClose = () => {
    if (fromProjectIdRef.current) {
      navigate(`/projects/${fromProjectIdRef.current}`);
    } else {
      navigate("/deliverable");
    }
  };

  const guardedClose = () => {
    if (isEditMode) {
      pendingNavigationRef.current = fromProjectIdRef.current
        ? `/projects/${fromProjectIdRef.current}`
        : "/deliverable";
      setShowNavGuard(true);
      return;
    }
    handleClose();
  };

  const sidebarUsername = localStorage.getItem(SESSION_USERNAME) ?? "";
  const handleLogout = () => {
    const logOut: LogOut = {
      token: localStorage.getItem(TOKEN_KEY),
      refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY),
      isConsentAccepted: null,
    };
    LogoutService(logOut);
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login");
  };
  const sidebarDropdownItems: MenuProps["items"] = [
    { key: "1", label: sidebarUsername, disabled: true },
    {
      key: "2",
      label: "Log out",
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ];

  const sidebarRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const toggleProjectMenu = () => {
    setIsMenuOpened((prevState) => !prevState);
  };
  const [count, setCount] = useState<number>(0);
  const handleIncrement = () => {
    setCount((prev) => {
      if (prev < 500) {
        return prev + 1;
      }
      return prev;
    });
  };
  const handleDecrement = () => setCount((prev) => (prev > 0 ? prev - 1 : 0));
  useEffect(() => {
    const handleDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.closest(`.${layout.sheetNameGroup}`) ||
        target.closest(`.${styles.nav}`)
      ) {
        return;
      }
      setIsMenuOpened(false);
    };
    document.addEventListener("click", handleDocClick);
    return () => document.removeEventListener("click", handleDocClick);
  }, []);
  const handleClickOutside = (event: MouseEvent) => {
    if (
      sidebarRef.current &&
      !sidebarRef.current.contains(event.target as Node)
    ) {
      setIsSidebarVisible(false);
    }
  };
  useEffect(() => {
    if (isSidebarVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const escFunction = useCallback((event: any) => {
    if (event.key === "Escape") {
      setIsMenuOpened(false);
    }
  }, []);
  useEffect(() => {
    const foundSheet = deliverableMenuDetails.find(
      (sheet) => sheet.sheetName === sheetNameFromParams,
    );

    if (foundSheet) {
      navigate(
        {
          pathname: `/deliverable/${deliverableId}/${foundSheet.sheetId}`,
          search: `?${ACTIVE_TABS_KEY}=${
            foundSheet.sheetName
          }&${NAME_KEY}=${deliverableName}${
            foundSheet.multiSheet ? "&" + MULTI_SHEET_KEY + "=true" : ""
          }`,
        },
        { replace: true },
      );
      setIsMultisheet(foundSheet.multiSheet);
      setSelectedLink(foundSheet.sheetName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetNameFromParams]);

  useEffect(() => {
    document.addEventListener("keydown", escFunction, false);
    return () => {
      document.removeEventListener("keydown", escFunction, false);
    };
  }, [escFunction]);

  useEffect(() => {
    const activetab = getActiveTab(location.search);
    setDeliverableName(getDeliverableName(location.search));
    setSelectedLink(activetab);
    fetchData();
    fetchDeliverableMenuDetails();
    // Check if location state contains selectedLink
    const { state } = location;
    if (state?.selectedLink) {
      setSelectedLink(state.selectedLink);
    }
    localStorage.removeItem("FilterKey");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const fetchData = async (): Promise<void> => {
    try {
      const response =
        await projectService.getDeliverableDetailsById<DeliverableMenuDetailsResponse>(
          Number(deliverableId),
        );
      setDeliverableDetails(response);
      setDeliverableName(response?.name);
    } catch (error) {
      message.error(TOAST_DEFAULT_ERROR_MESSAGE);
    }
  };

  const fetchDeliverableMenuDetails = async (): Promise<void> => {
    try {
      const response = await projectService.getDeliverableMenuDetails<
        DeliverableMenuSheet[]
      >(Number(deliverableId));
      setDeliverableMenuDetails(response);
    } catch (error) {
      message.error(TOAST_DEFAULT_ERROR_MESSAGE);
    }
  };

  const getActiveTab = (search: string): string => {
    const params = new URLSearchParams(search);
    return params.get(ACTIVE_TABS_KEY) ?? DASHBOARD;
  };
  const getDeliverableName = (search: string): string => {
    const params = new URLSearchParams(search);
    return params.get(NAME_KEY) ?? "";
  };
  const handleTemplateLinkClick = (
    linkName: string,
    sheet?: DeliverableMenuSheet,
  ) => {
    setSelectedLink(linkName);
    setIsMultisheet(sheet?.multiSheet ?? false);
  };
  //Function to display Ok button after successfull completion of import task.
  const handleImportSuccess = async () => {
    message.success("Import successful");
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSaveClick = () => {
    console.log(spreadWrapperRef.current);
    if (spreadWrapperRef.current) {
      spreadWrapperRef.current.saveClick();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAddRowClick = () => {
    setCount(0);
    setShowAddNewRow(true);
  };

  const handleAddNewRowClick = () => {
    setShowAddNewRow(false);
    if (agGridRef.current) {
      agGridRef.current.addRow(count);
      setCount(0);
    }
  };

  const handleEditOnlineClick = () => {
    setLoading("editOnline", true);
    try {
      setIsEditMode(true);
      if (agGridRef.current) agGridRef.current.editOnline();
    } finally {
      setLoading("editOnline", false);
    }
  };

  const handleAgGridSaveClick = async () => {
    setLoading("save", true);
    try {
      if (agGridRef.current) await agGridRef.current.save();
      message.success("Saved successfully");
      setIsEditMode(false);
    } catch {
      message.error("Save failed");
    } finally {
      setLoading("save", false);
    }
  };

  const handleCancelClick = () => {
    setLoading("cancel", true);
    try {
      if (agGridRef.current) agGridRef.current.cancel();
      setIsEditMode(false);
    } finally {
      setLoading("cancel", false);
    }
  };

  const handleAgGridExportClick = async () => {
    setLoading("export", true);
    try {
      if (agGridRef.current) await agGridRef.current.exportFile();
    } finally {
      setLoading("export", false);
    }
  };

  const handleAgGridAddRowClick = () => {
    setCount(0);
    setShowAddNewRow(true);
  };

  const handleAgGridImportClick = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!(e.target.files && agGridRef.current)) return;
    setLoading("import", true);
    message.loading({ content: "Importing...", key: "import", duration: 0 });
    try {
      await agGridRef.current.importFile(e.target.files[0]);
      message.success({ content: "Import successful", key: "import" });
      await agGridRef.current.refetchData();
    } catch {
      message.error({ content: "Import failed", key: "import" });
    } finally {
      setLoading("import", false);
    }
  };

  const [showNavGuard, setShowNavGuard] = useState(false);
  const pendingNavigationRef = useRef<string | null>(null);
  const isEditModeRef = useRef(isEditMode);
  isEditModeRef.current = isEditMode;

  // Block browser back/forward via popstate
  useEffect(() => {
    if (!isEditMode) return;

    // Push a duplicate entry so pressing back stays on the same page
    window.history.pushState(null, "", window.location.href);

    const handlePopState = () => {
      if (isEditModeRef.current) {
        // Re-push to prevent leaving
        window.history.pushState(null, "", window.location.href);
        pendingNavigationRef.current = "__browser_back__";
        setShowNavGuard(true);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isEditMode]);

  // Block page refresh / tab close
  useEffect(() => {
    if (!isEditMode) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isEditMode]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const guardedNavigate = useCallback(
    (to: string | any, options?: any) => {
      if (isEditModeRef.current) {
        pendingNavigationRef.current =
          typeof to === "string" ? to : JSON.stringify(to);
        setShowNavGuard(true);
        return;
      }
      navigate(to, options);
    },
    [navigate],
  );

  const handleNavSave = () => {
    if (agGridRef.current) {
      agGridRef.current.save().catch(() => {});
    }
    setIsEditMode(false);
    setShowNavGuard(false);
    const pending = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    if (pending && pending !== "__browser_back__") {
      try {
        const parsed = JSON.parse(pending);
        navigate(parsed);
      } catch {
        navigate(pending);
      }
    } else if (pending === "__browser_back__") {
      window.history.back();
    }
  };

  const handleNavCancel = () => {
    if (agGridRef.current) {
      agGridRef.current.cancel();
    }
    setIsEditMode(false);
    setShowNavGuard(false);
    const pending = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    if (pending && pending !== "__browser_back__") {
      try {
        const parsed = JSON.parse(pending);
        navigate(parsed);
      } catch {
        navigate(pending);
      }
    } else if (pending === "__browser_back__") {
      window.history.back();
    }
  };

  useEffect(() => {
    if (agGridRef.current) {
      agGridRef.current.search(searchValue);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  //Function to handle Import functionality to import excel files
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleImportButtonClick = async (e: ChangeEvent<HTMLInputElement>) => {
    message.loading({
      content: "Importing...",
      key: "legacyImport",
      duration: 0,
    });
    if (e.target.files) {
      const file = e.target.files[0];

      try {
        let importResponse = await projectService.import<any>(
          deliverableId,
          sheetId,
          file,
          isMultisheet,
        );
        if (importResponse.TaskId) {
          await pollForTaskStatus(handleImportSuccess, importResponse.TaskId);
        }
      } catch (error) {
        message.error({ content: "Import failed", key: "legacyImport" });
        return;
      }
    }
  };

  //Function to handle poll for getting task status while importing
  const pollForTaskStatus = async (
    successCallback: Function | null,
    taskid?: number,
  ) => {
    return new Promise(async (resolve, reject) => {
      const interval: number = parseInt(TASK_POLLING_INTERVAL.toString());

      const handleTaskStatus = (status: string) => {
        const sheet = deliverableMenuDetails.find(
          (sheet) => sheet.sheetId === Number(sheetId),
        );
        const sheetName = sheet ? sheet.sheetName : "";
        switch (status) {
          case TaskStatus.COMPLETED:
            clearInterval(poll);
            successCallback?.();
            message.success({
              content: "Import successful",
              key: "legacyImport",
            });
            resolve(null);
            let url = `/deliverable/${deliverableId}/${sheetId}?${ACTIVE_TABS_KEY}=${decodeURI(
              sheetName,
            )}&${NAME_KEY}=${deliverableName}`;
            if (sheet?.multiSheet) {
              url += "&multiSheet=true";
            }

            navigate(url, { state: { refreshPage: true } });

            if (window.location.href.includes(`${deliverableId}/template`)) {
              navigate(
                `/deliverable/${deliverableId}/template?${ACTIVE_TABS_KEY}=${FILTER_TEMPLATE_LABEL}`,
                { state: { refreshPage: true } },
              );
            }
            break;
          case TaskStatus.FAILED:
            clearInterval(poll);
            message.error({ content: "Import failed", key: "legacyImport" });
            reject(new Error(IMPORT_FAILED_MESSAGE));
            break;
          default:
            break;
        }
      };

      const fetchTaskStatus = async () => {
        try {
          const taskStatus =
            sheetId === TEMPLATE
              ? await projectService.taskStatus<any>(
                  deliverableId,
                  undefined,
                  taskid,
                  TaskType.RERUN,
                )
              : await projectService.taskStatus<any>(
                  deliverableId,
                  sheetId,
                  taskid,
                );

          if (!taskStatus) {
            successCallback?.();
            clearInterval(poll);
            return;
          }

          const status = (taskStatus?.Status ?? "").toLowerCase();
          handleTaskStatus(status);
        } catch (error) {
          clearInterval(poll);
          reject(new Error(error as any));
        }
      };

      // Define a function to handle errors from fetchTaskStatus
      const handleError = (error: any) => {
        clearInterval(poll);
        reject(new Error(error));
      };
      const poll = setInterval(() => {
        fetchTaskStatus().catch(handleError);
      }, interval);
    });
  };

  const closeModal = () => {
    setShowAddRow(false);
    setShowAddNewRow(false);
    setCount(0);
    form.resetFields();
  };
  //Function to handle Export Functionality of the Excel file (To downlod excel file when click on export button)
  const handleExport = async () => {
    setShowAddRow(false);
    const defaultFilterValues = generateDefaultFilterValues(uniqueSequenceDtos);
    const storedFilterKeys = getFilteredKeys();

    const selectedKeys = storedFilterKeys ?? defaultFilterValues;
    let fileName = "example.xlsx";
    try {
      fileName = `${searchParams.get(ACTIVE_TABS_KEY)}.xlsx`;
      await onHandleExportClick(
        deliverableId,
        sheetId,
        fileName,
        selectedKeys,
        count,
      );
      setCount(0);
    } catch (error) {
      message.error(TOAST_DEFAULT_ERROR_MESSAGE);
      return;
    }
  };
  const modalTitle = (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <span>Add Additional Row</span>
      <div style={{ display: "flex", gap: 8 }}>
        <Button size="small" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={showAddNewRow ? handleAddNewRowClick : handleExport}
        >
          Add
        </Button>
      </div>
    </div>
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const openProjectMenu = () => setIsMenuOpened(true);

  return (
    <div className={layout.pageLayout}>
      {/* ── Collapsed Sidebar ── */}
      <aside className={layout.sidebar}>
        <div className={layout.sidebarLogo}>
          <img
            className={layout.logoIcon}
            src="/propel-logo-white.png"
            alt="Propel"
            onClick={guardedClose}
          />
        </div>
        <div className={layout.sidebarSpacer} />
        <div className={layout.sidebarFooter}>
          <Dropdown menu={{ items: sidebarDropdownItems }} trigger={["click"]}>
            <img
              className={layout.profileAvatar}
              src="/profile-avatar.png"
              alt="Profile"
            />
          </Dropdown>
        </div>
      </aside>

      {/* ── Content Panel ── */}
      <div className={layout.contentPanel}>
        {/* Breadcrumb strip */}
        <div className={layout.breadcrumbStrip}>
          <span onClick={guardedClose}>{deliverableName || "Deliverable"}</span>
        </div>

        {/* Glassmorphic content area */}
        <div className={layout.glassContent}>
          {/* Title Row */}
          <div className={layout.titleRow}>
            <div className={layout.pageTitle}>
              {deliverableName}
              {isEditMode && (
                <span className={layout.editingLabel}>
                  {" "}
                  &mdash; Editing {sheetNameFromParams}
                </span>
              )}
            </div>
            <button
              className={layout.btnClose}
              onClick={guardedClose}
              title="Close"
            >
              <CloseOutlined style={{ fontSize: 14 }} />
            </button>
          </div>

          {/* Sheet Row */}
          <div className={layout.sheetRow}>
            <Dropdown
              open={isMenuOpened}
              menu={{}}
              dropdownRender={() => (
                <nav className={styles.nav}>
                  <div className={styles.left}>
                    <ul>
                      {deliverableMenuDetails.map((sheet) => (
                        <li
                          className={styles.navItems}
                          key={sheet.sheetId}
                          style={
                            selectedLink === sheet.sheetName
                              ? { background: "#F26522", color: "white" }
                              : {}
                          }
                          onClick={() => {
                            handleTemplateLinkClick(sheet.sheetName, sheet);
                            setIsMenuOpened(false);
                            let url = `/deliverable/${deliverableId}/${sheet.sheetId}?${ACTIVE_TABS_KEY}=${sheet.sheetName}&${NAME_KEY}=${deliverableName}`;
                            if (sheet.multiSheet) {
                              url += `&${MULTI_SHEET_KEY}=true`;
                            }
                            navigate(url, {
                              replace: true,
                              state: {
                                fromProjectId: fromProjectIdRef.current,
                              },
                            });
                          }}
                        >
                          {sheet.sheetName}
                        </li>
                      ))}
                      {deliverableDetails?.canWrite && (
                        <>
                          <li
                            style={{
                              borderTop: "1px solid #e5e7eb",
                              margin: "4px 0",
                            }}
                          />
                          <li
                            className={styles.navItems}
                            style={
                              selectedLink === USER_ACCESS
                                ? { background: "#F26522", color: "white" }
                                : {}
                            }
                            onClick={() => {
                              setIsMenuOpened(false);
                              handleTemplateLinkClick(USER_ACCESS);
                              navigate(
                                `/deliverable/user-access/${deliverableId}?${ACTIVE_TABS_KEY}=${USER_ACCESS}`,
                              );
                            }}
                          >
                            {USER_ACCESS}
                          </li>
                        </>
                      )}
                    </ul>
                  </div>
                </nav>
              )}
            >
              <div
                className={layout.sheetNameGroup}
                onClick={() => setIsMenuOpened(true)}
              >
                <span className={layout.hamburgerIcon}>
                  <MenuOutlined />
                </span>
                <span className={layout.sheetName}>{selectedLink}</span>
              </div>
            </Dropdown>

            <div className={layout.sheetActions}>
              {selectedLink &&
                selectedLink !== USER_ACCESS &&
                additionalTabsVisible && (
                  <>
                    <SearchInput
                      onSearch={(value) => setSearchValue(value)}
                      placeholder="Search"
                    />
                    {isEditMode ? (
                      <>
                        <button
                          className={layout.btnOutline}
                          disabled={!hasImportPermission}
                          onClick={handleAgGridAddRowClick}
                        >
                          <BtnContent
                            loading={!!loadingStates.addRow}
                            label={ADDROW_TEXT}
                          />
                        </button>
                        <button
                          className={layout.btnPrimary}
                          disabled={
                            !hasImportPermission || !!loadingStates.save
                          }
                          onClick={handleAgGridSaveClick}
                        >
                          <BtnContent
                            loading={!!loadingStates.save}
                            label={SAVE_TEXT}
                          />
                        </button>
                        <button
                          className={layout.btnOutline}
                          onClick={handleCancelClick}
                        >
                          <BtnContent
                            loading={!!loadingStates.cancel}
                            label={CANCEL_TEXT}
                          />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className={layout.btnPrimary}
                          disabled={!hasImportPermission}
                          onClick={handleEditOnlineClick}
                        >
                          <BtnContent
                            loading={!!loadingStates.editOnline}
                            label={EDIT_ONLINE_TEXT}
                          />
                        </button>
                        <label
                          className={`${layout.btnPrimary} ${layout.importLabel}`}
                        >
                          <input
                            type="file"
                            title=" "
                            onChange={(e) => {
                              handleAgGridImportClick(e);
                              e.target.value = "";
                            }}
                            accept=".xlsx"
                            disabled={
                              !hasImportPermission || !!loadingStates.import
                            }
                          />
                          <BtnContent
                            loading={!!loadingStates.import}
                            label={IMPORT_BUTTON_LABEL}
                          />
                        </label>
                        <button
                          className={layout.btnPrimary}
                          onClick={
                            hasImportPermission
                              ? handleAgGridExportClick
                              : () => {}
                          }
                          disabled={
                            !hasImportPermission || !!loadingStates.export
                          }
                        >
                          <BtnContent
                            loading={!!loadingStates.export}
                            label={EXPORT_BUTTON_LABEL}
                          />
                        </button>
                      </>
                    )}
                  </>
                )}
            </div>
          </div>

          {/* AG Grid — bleeding container */}
          <div className={layout.gridContainer}>
            <Outlet />
          </div>
        </div>
      </div>

      {/* ── Modals (kept as-is) ── */}
      <Modal
        title={<span style={MODAL_TITLE_STYLE}>{modalTitle}</span>}
        open={showAddRow || showAddNewRow}
        footer={null}
        closable={false}
        styles={MODAL_STYLES}
      >
        <ConfigProvider theme={darkFormTheme}>
        <Form className="addRowForm" form={form} layout="vertical">
          <Form.Item name="addRow" required>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                justifyContent: "center",
                paddingTop: "20px",
              }}
            >
              <Button type="default" onClick={handleDecrement}>
                -
              </Button>
              <Input
                style={{ width: 200, textAlign: "center" }}
                value={count}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (!isNaN(val) && val >= 0) setCount(val);
                  if (!isNaN(val) && val >= 500) setCount(500);
                }}
              />
              <Button type="default" onClick={handleIncrement}>
                +
              </Button>
            </div>
          </Form.Item>
        </Form>
        </ConfigProvider>
      </Modal>
      <Modal
        title={<span style={MODAL_TITLE_STYLE}>Unsaved Changes</span>}
        open={showNavGuard}
        footer={[
          <Button key="cancel" onClick={handleNavCancel}>
            {CANCEL_TEXT}
          </Button>,
          <Button key="save" type="primary" onClick={handleNavSave}>
            {SAVE_TEXT}
          </Button>,
        ]}
        closable={false}
        maskClosable={false}
        styles={MODAL_STYLES}
      >
        <p>You have unsaved changes. Would you like to save before leaving?</p>
      </Modal>
    </div>
  );
};
